import type { CulinaryLanguage, LocalizationLanguageTag } from '../../../shared/culinary-locales';
import type {
  IngredientSummary, RecipeDetail, RecipeScaleRequest, RecipeScaleResult, RecipeSummary, RecipeVersionOption, ScalingMode, UsageReference
} from '../../../shared/domain';
import type { KitchenValidationAssessment } from '../../../shared/kitchen-tools';
import type { LanguageProfile } from '../../../shared/localization';
import { jsonBody, requestJson } from '../../shared/api-client';

export interface RecipeDraftPayload {
  revision: number; title: string; primaryLanguage: CulinaryLanguage; yieldAmount: number | null; yieldUnit: string | null; portionCount: number | null;
  batchMaxYieldAmount: number | null; batchMaxYieldUnit: string | null; notes: string;
  components: Array<{
    id?: string | null; componentType: 'ingredient' | 'recipe'; ingredientId: string | null; referencedRecipeVersionId: string | null; sourceLanguage: CulinaryLanguage;
    detectedLanguages: CulinaryLanguage[];
    rawQuantity: string; rawUnit: string; quantity: number | null; unitCode: string | null; groupName: string; note: string; scalingMode: ScalingMode;
  }>;
  steps: Array<{
    id?: string | null; sourceLanguage: CulinaryLanguage; detectedLanguages: CulinaryLanguage[]; sectionName: string; instruction: string; note: string;
    componentRefs: string[];
  }>;
}

export interface RecipeGroupView {
  id: string;
  name: string;
  recipeIds: string[];
}

export interface RecipeGroupAdjustOperation {
  groupId: string;
  present: boolean;
}

export interface RecipeGroupAdjustInput {
  recipeIds: string[];
  operations: RecipeGroupAdjustOperation[];
  createAndAdd?: string;
}

export interface RecipeLocalizationPayload {
  title: string; notes: string;
  components: Array<{ componentId: string; sortOrder: number; displayName: string; groupName: string; note: string }>;
  steps: Array<{ stepId: string; sortOrder: number; sectionName: string; instruction: string; note: string }>;
}

export const recipeApi = {
  list: () => requestJson<{ recipes: RecipeSummary[] }>('/api/recipes'),
  detail: (id: string) => requestJson<{ recipe: RecipeDetail }>(`/api/recipes/${encodeURIComponent(id)}`),
  create: (name: string) => requestJson<{ id: string }>('/api/recipes', jsonBody('POST', { name })),
  groups: () => requestJson<{ groups: RecipeGroupView[] }>('/api/recipes/groups'),
  createGroup: (name: string) => requestJson<{ groups: RecipeGroupView[] }>('/api/recipes/groups', jsonBody('POST', { name })),
  renameGroup: (groupId: string, name: string) =>
    requestJson<{ groups: RecipeGroupView[] }>(`/api/recipes/groups/${encodeURIComponent(groupId)}`, jsonBody('PATCH', { name })),
  deleteGroup: (groupId: string) =>
    requestJson<{ groups: RecipeGroupView[] }>(`/api/recipes/groups/${encodeURIComponent(groupId)}`, { method: 'DELETE' }),
  adjustGroups: (input: RecipeGroupAdjustInput) =>
    requestJson<{ groups: RecipeGroupView[] }>('/api/recipes/groups/adjust', jsonBody('POST', input)),
  save: (id: string, input: RecipeDraftPayload) => requestJson<{ ok: true }>(`/api/recipes/${encodeURIComponent(id)}/draft`, jsonBody('PATCH', input)),
  localizationConfig: () => requestJson<{ configured: boolean }>('/api/recipes/localization-config'),
  generateLocalization: (recipeId: string, versionId: string, language: LocalizationLanguageTag) => requestJson<{ ok: true }>(`/api/recipes/${encodeURIComponent(recipeId)}/versions/${encodeURIComponent(versionId)}/localizations/${encodeURIComponent(language)}/generate`, jsonBody('POST', {})),
  localize: (recipeId: string, versionId: string, language: LocalizationLanguageTag, input: RecipeLocalizationPayload) => requestJson<{ ok: true }>(`/api/recipes/${encodeURIComponent(recipeId)}/versions/${encodeURIComponent(versionId)}/localizations/${encodeURIComponent(language)}`, jsonBody('PATCH', input)),
  deleteLocalization: (recipeId: string, versionId: string, language: LocalizationLanguageTag) => requestJson<{ ok: true }>(`/api/recipes/${encodeURIComponent(recipeId)}/versions/${encodeURIComponent(versionId)}/localizations/${encodeURIComponent(language)}`, { method: 'DELETE' }),
  publish: (id: string, revision: number) => requestJson<{ ok: true }>(`/api/recipes/${encodeURIComponent(id)}/publish`, jsonBody('POST', { revision })),
  revise: (id: string) => requestJson<{ id: string }>(`/api/recipes/${encodeURIComponent(id)}/revise`, jsonBody('POST', {})),
  versions: () => requestJson<{ recipes: RecipeVersionOption[] }>('/api/recipes/options/versions'),
  usage: (id: string) => requestJson<{ usage: UsageReference[] }>(`/api/recipes/${encodeURIComponent(id)}/usage`),
  scale: (recipeId: string, versionId: string, input: RecipeScaleRequest) => requestJson<{ result: RecipeScaleResult; validation: KitchenValidationAssessment | null }>(`/api/recipes/${encodeURIComponent(recipeId)}/versions/${encodeURIComponent(versionId)}/scale`, jsonBody('POST', input)),
  copy: (id: string, versionId: string, format: string, scaleRequest?: RecipeScaleRequest, languageProfile: LanguageProfile = 'original') => requestJson<{ text: string }>(`/api/recipes/${encodeURIComponent(id)}/copy`, jsonBody('POST', { versionId, format, scaleRequest, languageProfile })),
  ingredients: (q = '') => requestJson<{ ingredients: IngredientSummary[] }>(`/api/ingredients${q ? `?q=${encodeURIComponent(q)}` : ''}`)
};
